import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { formatPhoneNumber, formatPhoneNumberIntl, getCountries, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input/input';
import PhoneInputCountrySelect from 'react-phone-number-input';
import ru from 'react-phone-number-input/locale/ru';
import ErrorHint from './ErrorHint';

export const PHONE_FORMAT_COUNTRY = getCountries().reduce((acc, country) => { acc[country] = country; return acc; }, {});

const Phone = ({ value = 0, className = '', international = true, brackets = true }) => {
  let phone = international ? formatPhoneNumberIntl(value) : formatPhoneNumber(value);
  let reg = /[+]*(\d+)[ (]+(\d+)[ )]+(\d+)[ -](\d+)[ -](\d+)/;

  if (brackets && international) {
    if (reg.test(phone)) {
      const m = phone.match(reg);
      phone = `+${m[1]} (${m[2]}) ${m[3]}-${m[4]}-${m[5]}`;
    }
  } else if (!brackets && !international) {
    if (reg.test(phone)) {
      const m = phone.match(reg);
      phone = `${m[1]} ${m[2]} ${m[3]} ${m[4]} ${m[5]}`;
    }
  }

  return (
    <span className={`phone ${className}`}>{phone}</span>
  );
};

Phone.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string.isRequired,
  international: PropTypes.bool,
  brackets: PropTypes.bool
};

const parsePhone = (phone, country) => {
  let parsed = parsePhoneNumber(phone, country);
  return parsed ? parsed.number : phone;
};

const Input = ({ className = '', name, label, placeholder, error, value = '', onInput, onChange, onEnter, country = PHONE_FORMAT_COUNTRY.RU, reff, children }) => {
  const [phone, setPhone] = useState('');


  useEffect(() => {
    const validPhone = value ? (isValidPhoneNumber(value) ? value : parsePhone(value, country)) : '';
    setPhone(validPhone);
    value && onChange && onChange(validPhone);
  }, []);

  useEffect(() => {
    const validPhone = value ? (isValidPhoneNumber(value) ? value : parsePhone(value, country)) : '';
    setPhone(validPhone);
  }, [value]);

  return (
    <>
      {label && (
        <span className="field-label">{label}</span>
      )}
      <div className="input-group">
        <PhoneInputCountrySelect
          className={`text ${className}`}
          value={phone}
          labels={ru}
          defaultCountry={country}
          placeholder={placeholder}
          name={name}
          onInput={(e) => {
            onInput && onInput(parsePhone(e.target.value, country));
          }}
          international={true}
          ref={reff}
          onChange={(val) => {
            onChange && onChange(val);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && typeof onEnter === 'function') {
              e && e.preventDefault();
              onEnter && onEnter(e.target.value.trim());
              onChange && onChange(parsePhone(e.target.value, country));
            }
          }}
        />
        {children}
      </div>
      {error && (
        <ErrorHint error={error} />
      )}
    </>
  );
};

Input.propTypes = {
  className: PropTypes.string,
  value: PropTypes.any,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  country: PropTypes.string,
  onInput: PropTypes.func,
  onChange: PropTypes.func,
  onEnter: PropTypes.func,
  reff: PropTypes.any
};

Phone.Input = Input;

class Input2 extends React.Component {
  constructor(props) {
    super(props);
    this.onInput = typeof props.onInput === 'function' ? props.onInput : () => {};
    this.onChange = typeof props.onChange === 'function' ? props.onChange : () => {};

    this.state = {
      phone: props.value,
      country: props.country ?? PHONE_FORMAT_COUNTRY.RU
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.value !== this.state.phone) {
      this.setState({ ...prevProps, phone: this.props.value });
    }
  }

  inputValue(value, target) {
    this.setState((prevProps) => ({ ...prevProps, phone: value }));
    this.props.onInput && this.props.onInput(value, target);
  };

  changeValue(value) {
    this.setState((prevProps) => ({ ...prevProps, phone: value }));
    this.onChange(value);
  };

  changeCountry(country) {
    this.setState((prevProps) => ({ ...prevProps, country: country }));
  };

  keydown(e) {
    if (e.key === "Enter" && typeof onEnter === 'function') {
      e && e.preventDefault();
      this.props.onEnter && this.props.onEnter(e.target.value.trim());
      this.onChange && this.onChange(parsePhone(e.target.value, this.state.country));
    }
  };

  render() {
    return (
      <>
        {this.props.label && (
          <span className="field-label">{this.props.label}</span>
        )}
        <div className="input-group">
          <PhoneInputCountrySelect
            className={`text ${this.props.className || ''}`}
            value={this.state.phone}
            labels={ru}
            defaultCountry={this.state.country}
            placeholder={this.props.placeholder}
            name={this.props.name}
            onInput={(e) => this.inputValue(parsePhone(e.target.value, this.state.country), e.target)}
            international={true}
            ref={this.props.reff}
            onChange={(val) => this.changeValue(val)}
            onKeyDown={(e) => this.keydown(e)}
            onCountryChange={(country) => this.changeCountry(country)}
          />
          {this.props.children}
        </div>

        {this.props.error && (
          <ErrorHint error={this.props.error} />
        )}
      </>
    );
  }
}

Input2.propTypes = {
  className: PropTypes.string,
  max: PropTypes.number,
  min: PropTypes.number,
  note: PropTypes.string,
  error: PropTypes.string,
  onInput: PropTypes.func,
  onChange: PropTypes.func,
  currency: PropTypes.string,
  placeholder: PropTypes.any,
};

Phone.Input2 = Input2;

const Text = ({ phone, international = true, brackets = true }) => {
  phone = international ? formatPhoneNumberIntl(phone) : formatPhoneNumber(phone);
  let reg = /[+]*(\d+)[ (]+(\d+)[ )]+(\d+)[ -](\d+)[ -](\d+)/;

  if (brackets && international) {
    if (reg.test(phone)) {
      const m = phone.match(reg);
      phone = `+${m[1]} (${m[2]}) ${m[3]}-${m[4]}-${m[5]}`;
    }
  } else if (!brackets && !international) {
    if (reg.test(phone)) {
      const m = phone.match(reg);
      phone = `${m[1]} ${m[2]} ${m[3]} ${m[4]} ${m[5]}`;
    }
  }

  return phone;
};

Text.propTypes = {
  phone: PropTypes.string,
  international: PropTypes.bool,
  brackets: PropTypes.bool
};

Phone.Text = Text;

const Call = ({ value = 0, className = '', international = true, brackets = true }) => {
  return (
    <a href={`tel:${value}`} className={`phone-call ${className}`}><Phone value={value} className={className} international={international} brackets={brackets}/></a>
  );
};

Call.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string.isRequired,
  international: PropTypes.bool,
  brackets: PropTypes.bool
};

Phone.Call = Call;

Phone.validate = (value) => {
  return isValidPhoneNumber(value);
};

export default Phone;